import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useOverlayContext } from '@providers/OverlayProvider';
import OVERLAYS from '@constants/overlays';

import type { AiTreeSection } from './AiScripts/kind';
import {
  EditorFocus,
  EditorNodeKind,
  EditorSelectedNode,
} from './editorCategories';
import type { SortDirection } from './sortUtils';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// One entry per independently-sortable list in the tree — each sorts and toggles on its
// own, not in lockstep with the others.
export type SortableListId = 'spots' | 'hotspots' | 'objects' | 'aiScripts';

const DEFAULT_SORT_DIRECTIONS: Record<SortableListId, SortDirection> = {
  spots: 'asc',
  hotspots: 'asc',
  objects: 'asc',
  aiScripts: 'asc',
};

export interface EditorModeContextValue {
  selectedNode: EditorSelectedNode;
  selectMapAndColors: () => void;
  selectSpot: (ueIdentifier: string) => void;
  selectHotspot: (hotspotId: string) => void;
  selectRegionScript: (trigger: string, scriptId: string | null) => void;
  // Switches to the assistant focus, its Scripts section, and selects the
  // region there — from any focus. The focus change resets the selection after
  // commit, so a plain setActiveFocus + selectRegionScript pair loses the
  // selection; this parks it and applies it once the focus has changed.
  openRegionScript: (trigger: string) => void;
  selectMapLayout: () => void;
  // Selects one segment of the walk, by placement key. The entrance is START_ROOM_KEY — see
  // EditorSelectedNode. Opens the segment panel, which is the only place a segment is worked on.
  selectMapSegment: (roomKey: string) => void;
  selectBrandingEntity: (entityId: string) => void;
  selectRealtimeEntity: (entityId: string) => void;
  clearSelection: () => void;

  activeFocus: EditorFocus;
  setActiveFocus: (focus: EditorFocus) => void;

  // Whether this hub has asked Unreal for the walk and not yet asked to come back.
  //
  // A fallback source for the walk banner, and only that: the banner is driven off `EDITOR_MODE_STATE`
  // wherever there is one, because something other than the web can move the author and a banner
  // driven from a local flag would then describe a view they are not in.
  //
  // It exists because the two halves of the walk are not gated alike. **Take me there** is a control
  // and is always drawn, while the banner carrying the only way back reads the state message — and a
  // build that sends none is treated as supported everywhere else in this feature. Without this flag
  // such a build lets an author walk in and offers them nothing that walks back out.
  walkRequested: boolean;
  setWalkRequested: (requested: boolean) => void;

  // Which section of the assistant (AI Scripts) focus the left panel shows:
  // 'scripts' (AI triggers + spots, unified) or 'knowledge'. Lives here (not
  // local to the tree) because the UE selection bridges flip it on spot clicks
  // and on entering a live AI trigger.
  aiTreeSection: AiTreeSection;
  setAiTreeSection: (section: AiTreeSection) => void;

  // When true, walking into an AI trigger (SYNC_DATA_UE.audioTrigger.aiTriggerId)
  // auto-opens the inspector for that trigger instead of letting the virtual
  // assistant (Noa) play. Editor-only convenience; default off.
  followLiveTrigger: boolean;
  setFollowLiveTrigger: (value: boolean) => void;

  // Teleporting to a trigger (AiInspectorHeader's "teleport to trigger")
  // closes the Inspector, but the resulting real in-world region-enter
  // updates aiTriggerName shortly after — with followLiveTrigger on, that
  // would otherwise re-select the same trigger and reopen the pane right
  // after it was closed. Set to the trigger name right before teleporting;
  // AiTriggerSelectionBridge checks it and skips exactly one matching
  // re-selection, then clears it.
  suppressedTriggerSelectRef: React.MutableRefObject<string | null>;
  suppressNextTriggerSelect: (trigger: string) => void;

  isSceneTreeCollapsed: boolean;
  toggleSceneTree: () => void;

  isQuickSwitcherOpen: boolean;
  openQuickSwitcher: () => void;
  closeQuickSwitcher: () => void;

  autosaveStatus: AutosaveStatus;
  setAutosaveStatus: (status: AutosaveStatus) => void;

  // Bumped whenever entity values are written from outside the inspector — the
  // CSV import. The open inspector watches it and reloads, so the panel does
  // not keep showing the values the import has just replaced.
  brandingEntitiesTick: number;
  bumpBrandingEntitiesTick: () => void;

  // The inspector holds an edit for 600 ms before saving it. An import writes
  // the same record, so it must not race that save: the inspector registers its
  // flush here while mounted, and the importer awaits it before its own write.
  registerBrandingEntityFlush: (flush: (() => Promise<void>) | null) => void;
  flushBrandingEntitySave: () => Promise<void>;

  // Recent in-world spot clicks (UE → hub via CUSTOMISATION_ITEM_SELECTED).
  // Used by the AI Scripts create popover to suggest regions the user has
  // recently interacted with. Capped + deduped; reset between editor sessions.
  recentSceneClicks: string[];
  registerSceneClick: (id: string) => void;

  // Kept highlighted in the SceneTree after a teleport that also clears
  // selectedNode — Inspector.tsx's handleTeleportToSpot does this when the
  // destination region has an AI script, so the Inspector closes and the
  // assistant can take over the same screen area. Without this, the author
  // loses track of which spot they just teleported to (MV-1881). Cleared as
  // soon as a genuinely new node is explicitly selected.
  highlightedSpotUeId: string | null;
  pinSpotHighlight: (ueIdentifier: string) => void;

  // Ascending/descending direction per sortable list (spots, hotspots,
  // objects, AI scripts) — each toggles independently. In-memory for the
  // editing session — reset on reload or re-entering Editor Mode. The
  // control itself is a single button in the panel header that follows
  // whichever focus tab is active; only the underlying direction is per-list.
  sortDirections: Record<SortableListId, SortDirection>;
  toggleSortDirection: (listId: SortableListId) => void;

  // Whether the AI Scripts tree's active section (Scripts or Knowledge) has
  // any row to sort. The header's sort button lives in SceneTree, which has
  // no view of AiScriptsTreeSection's own bucket counts, so that component
  // reports this back as it changes. Defaults to `false` (not empty) so a
  // freshly-mounted tab shows the control rather than hiding a working one
  // for the one frame before the real count lands.
  isAiScriptsListEmpty: boolean;
  setIsAiScriptsListEmpty: (isEmpty: boolean) => void;

  // True while a spot's image/video is mid-upload in the Inspector — set by
  // SpotInspector. Every select* below no-ops while this is true, so the tree
  // can't be used to navigate away and abandon the in-flight upload's UI.
  isSpotMediaUploadLocked: boolean;
  setSpotMediaUploadLocked: (locked: boolean) => void;
}

const EditorModeContext = createContext<EditorModeContextValue | null>(null);

interface EditorModeProviderProps {
  children: ReactNode;
}

const getDefaultSelectionForFocus = (
  focus: EditorFocus,
): EditorSelectedNode => {
  if (focus === 'environment') {
    return { kind: EditorNodeKind.MAP_AND_COLORS };
  }
  // Like environment, the map editor has exactly one thing to edit — the layout —
  // so open its inspector straight away rather than making the author click a tree
  // row to reveal the only panel the focus has.
  if (focus === 'mapEditor') {
    return { kind: EditorNodeKind.MAP_LAYOUT };
  }
  return null;
};

/**
 * Holds the editor overlay's own UI state: what is selected, which panels are open, and how a save
 * is going.
 *
 * The active focus (tab) is deliberately not among them — it is read from and written back to
 * `brandingEditorFocus` in MytaverseEventProvider. The section picker sets that value from outside
 * this provider, which stays mounted for the whole editing session, so a local copy seeded from a
 * prop was only ever read once and picking a second section never moved the tab (MV-1912).
 *
 * @param props.children - The editor tree that consumes the context.
 */
export const EditorModeProvider = ({ children }: EditorModeProviderProps) => {
  const { brandingEditorFocus, setBrandingEditorFocus } = useMytaverseEvent();
  const [aiTreeSection, setAiTreeSection] = useState<AiTreeSection>('scripts');
  const [followLiveTrigger, setFollowLiveTrigger] = useState<boolean>(false);
  const suppressedTriggerSelectRef = useRef<string | null>(null);
  const suppressNextTriggerSelect = useCallback((trigger: string) => {
    suppressedTriggerSelectRef.current = trigger;
  }, []);
  const [selectedNode, setSelectedNode] = useState<EditorSelectedNode>(() =>
    getDefaultSelectionForFocus(brandingEditorFocus),
  );
  const [isSceneTreeCollapsed, setSceneTreeCollapsed] = useState(false);
  const [isQuickSwitcherOpen, setQuickSwitcherOpen] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('idle');
  const [walkRequested, setWalkRequested] = useState(false);
  const [recentSceneClicks, setRecentSceneClicks] = useState<string[]>([]);
  const [brandingEntitiesTick, setBrandingEntitiesTick] = useState(0);
  const [highlightedSpotUeId, setHighlightedSpotUeId] = useState<string | null>(
    null,
  );
  const [sortDirections, setSortDirections] = useState<
    Record<SortableListId, SortDirection>
  >(DEFAULT_SORT_DIRECTIONS);
  const [isAiScriptsListEmpty, setIsAiScriptsListEmpty] = useState(false);
  const [isSpotMediaUploadLocked, setSpotMediaUploadLocked] = useState(false);
  // A ref, not state: registering must not re-render the tree or change the
  // context value, which would remount the inspector that just registered.
  const brandingEntityFlushRef = useRef<(() => Promise<void>) | null>(null);

  const overlay = useOverlayContext();

  const closeExclusiveOverlays = useCallback(() => {
    overlay?.removeFromCurrentlyOpenOverlays?.(OVERLAYS.ADMIN_TOOLS);
    overlay?.removeFromCurrentlyOpenOverlays?.(OVERLAYS.PROFILE);
  }, [overlay]);

  const registerSceneClick = useCallback((id: string) => {
    if (!id) {
      return;
    }
    setRecentSceneClicks((prev) => {
      const trimmed = id.trim();
      const filtered = prev.filter((existing) => existing !== trimmed);
      // Newest first, capped to keep the create popover from growing unbounded.
      return [trimmed, ...filtered].slice(0, 8);
    });
  }, []);

  const selectMapAndColors = useCallback(() => {
    if (isSpotMediaUploadLocked) {
      return;
    }
    closeExclusiveOverlays();
    setSelectedNode({ kind: EditorNodeKind.MAP_AND_COLORS });
  }, [closeExclusiveOverlays, isSpotMediaUploadLocked]);

  const selectSpot = useCallback(
    (ueIdentifier: string) => {
      if (isSpotMediaUploadLocked) {
        return;
      }
      closeExclusiveOverlays();
      setSelectedNode({ kind: EditorNodeKind.SPOT, ueIdentifier });
    },
    [closeExclusiveOverlays, isSpotMediaUploadLocked],
  );

  const selectHotspot = useCallback(
    (hotspotId: string) => {
      if (isSpotMediaUploadLocked) {
        return;
      }
      closeExclusiveOverlays();
      setSelectedNode({ kind: EditorNodeKind.HOTSPOT, hotspotId });
    },
    [closeExclusiveOverlays, isSpotMediaUploadLocked],
  );

  const selectRegionScript = useCallback(
    (trigger: string, scriptId: string | null) => {
      if (isSpotMediaUploadLocked) {
        return;
      }
      closeExclusiveOverlays();
      setSelectedNode({
        kind: EditorNodeKind.REGION_SCRIPT,
        trigger,
        scriptId,
      });
    },
    [closeExclusiveOverlays, isSpotMediaUploadLocked],
  );

  const selectMapLayout = useCallback(() => {
    if (isSpotMediaUploadLocked) {
      return;
    }
    closeExclusiveOverlays();
    setSelectedNode({ kind: EditorNodeKind.MAP_LAYOUT });
  }, [closeExclusiveOverlays, isSpotMediaUploadLocked]);

  const selectMapSegment = useCallback(
    (roomKey: string) => {
      if (!roomKey || isSpotMediaUploadLocked) {
        return;
      }
      closeExclusiveOverlays();
      setSelectedNode({ kind: EditorNodeKind.MAP_SEGMENT, roomKey });
    },
    [closeExclusiveOverlays, isSpotMediaUploadLocked],
  );

  const selectBrandingEntity = useCallback(
    (entityId: string) => {
      if (isSpotMediaUploadLocked) {
        return;
      }
      closeExclusiveOverlays();
      setSelectedNode({ kind: EditorNodeKind.BRANDING_ENTITY, entityId });
    },
    [closeExclusiveOverlays, isSpotMediaUploadLocked],
  );

  const selectRealtimeEntity = useCallback(
    (entityId: string) => {
      closeExclusiveOverlays();
      setSelectedNode({ kind: EditorNodeKind.REALTIME_ENTITY, entityId });
    },
    [closeExclusiveOverlays],
  );

  const clearSelection = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const setActiveFocus = useCallback(
    (focus: EditorFocus) => {
      // The effect below resets selectedNode on every focus change, which would
      // tear down a mid-upload SpotInspector just like the select* functions do.
      if (isSpotMediaUploadLocked) {
        return;
      }
      setBrandingEditorFocus(focus);
    },
    [setBrandingEditorFocus, isSpotMediaUploadLocked],
  );

  // A selection meant to survive a focus change, applied by the reset effect
  // below instead of the focus's default; null when nothing is parked.
  const pendingFocusSelectionRef = useRef<EditorSelectedNode>(null);

  const openRegionScript = useCallback(
    (trigger: string) => {
      if (isSpotMediaUploadLocked) {
        return;
      }
      setAiTreeSection('scripts');
      if (brandingEditorFocus === 'assistant') {
        selectRegionScript(trigger, null);
        return;
      }
      closeExclusiveOverlays();
      pendingFocusSelectionRef.current = {
        kind: EditorNodeKind.REGION_SCRIPT,
        trigger,
        scriptId: null,
      };
      setBrandingEditorFocus('assistant');
    },
    [
      brandingEditorFocus,
      closeExclusiveOverlays,
      isSpotMediaUploadLocked,
      selectRegionScript,
      setBrandingEditorFocus,
    ],
  );

  // Resetting the selection lives here rather than in setActiveFocus so both
  // ways of changing the tab behave the same — the tab strip inside the editor
  // and the section picker outside it, which writes the global value directly.
  // Seeded with the mount value so it does not fire on mount: selectedNode's
  // initializer above already produced the same selection, and re-setting it
  // would hand the subtree a fresh object identity for nothing.
  const prevFocusRef = useRef(brandingEditorFocus);
  useEffect(() => {
    if (prevFocusRef.current === brandingEditorFocus) {
      return;
    }
    prevFocusRef.current = brandingEditorFocus;
    const parked = pendingFocusSelectionRef.current;
    pendingFocusSelectionRef.current = null;
    setSelectedNode(parked ?? getDefaultSelectionForFocus(brandingEditorFocus));
  }, [brandingEditorFocus]);

  const pinSpotHighlight = useCallback((ueIdentifier: string) => {
    setHighlightedSpotUeId(ueIdentifier);
  }, []);

  // A pin only exists to survive the clearSelection() a teleport triggers, so once the author
  // makes a genuinely new selection elsewhere it is stale — drop it.
  useEffect(() => {
    if (selectedNode) {
      setHighlightedSpotUeId(null);
    }
  }, [selectedNode]);

  const toggleSceneTree = useCallback(() => {
    setSceneTreeCollapsed((prev) => !prev);
  }, []);

  const openQuickSwitcher = useCallback(() => {
    setQuickSwitcherOpen(true);
  }, []);

  const closeQuickSwitcher = useCallback(() => {
    setQuickSwitcherOpen(false);
  }, []);

  const bumpBrandingEntitiesTick = useCallback(() => {
    setBrandingEntitiesTick((prev) => prev + 1);
  }, []);

  const registerBrandingEntityFlush = useCallback(
    (flush: (() => Promise<void>) | null) => {
      brandingEntityFlushRef.current = flush;
    },
    [],
  );

  const flushBrandingEntitySave = useCallback(async () => {
    await brandingEntityFlushRef.current?.();
  }, []);

  const toggleSortDirection = useCallback((listId: SortableListId) => {
    setSortDirections((prev) => ({
      ...prev,
      [listId]: prev[listId] === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const value = useMemo<EditorModeContextValue>(
    () => ({
      selectedNode,
      selectMapAndColors,
      selectSpot,
      selectHotspot,
      selectRegionScript,
      openRegionScript,
      selectMapLayout,
      selectMapSegment,
      selectBrandingEntity,
      selectRealtimeEntity,
      clearSelection,
      activeFocus: brandingEditorFocus,
      setActiveFocus,
      walkRequested,
      setWalkRequested,
      aiTreeSection,
      setAiTreeSection,
      followLiveTrigger,
      setFollowLiveTrigger,
      suppressedTriggerSelectRef,
      suppressNextTriggerSelect,
      isSceneTreeCollapsed,
      toggleSceneTree,
      isQuickSwitcherOpen,
      openQuickSwitcher,
      closeQuickSwitcher,
      autosaveStatus,
      setAutosaveStatus,
      brandingEntitiesTick,
      bumpBrandingEntitiesTick,
      registerBrandingEntityFlush,
      flushBrandingEntitySave,
      recentSceneClicks,
      registerSceneClick,
      highlightedSpotUeId,
      pinSpotHighlight,
      sortDirections,
      toggleSortDirection,
      isAiScriptsListEmpty,
      setIsAiScriptsListEmpty,
      isSpotMediaUploadLocked,
      setSpotMediaUploadLocked,
    }),
    [
      selectedNode,
      selectMapAndColors,
      selectSpot,
      selectHotspot,
      selectRegionScript,
      openRegionScript,
      selectMapLayout,
      selectMapSegment,
      selectBrandingEntity,
      selectRealtimeEntity,
      clearSelection,
      brandingEditorFocus,
      setActiveFocus,
      walkRequested,
      aiTreeSection,
      followLiveTrigger,
      suppressNextTriggerSelect,
      isSceneTreeCollapsed,
      toggleSceneTree,
      isQuickSwitcherOpen,
      openQuickSwitcher,
      closeQuickSwitcher,
      autosaveStatus,
      brandingEntitiesTick,
      bumpBrandingEntitiesTick,
      registerBrandingEntityFlush,
      flushBrandingEntitySave,
      recentSceneClicks,
      registerSceneClick,
      highlightedSpotUeId,
      pinSpotHighlight,
      sortDirections,
      toggleSortDirection,
      isAiScriptsListEmpty,
      isSpotMediaUploadLocked,
    ],
  );

  return (
    <EditorModeContext.Provider value={value}>
      {children}
    </EditorModeContext.Provider>
  );
};

export const useEditorMode = (): EditorModeContextValue => {
  const ctx = useContext(EditorModeContext);
  if (!ctx) {
    throw new Error('useEditorMode must be used inside EditorModeProvider');
  }
  return ctx;
};
